/**
 * Who the viewer is allowed to be, in the frontend's terms.
 *
 * The product was only ever driven as an admin, so components gated on
 * `is_admin` alone and the Resilience Lead -- the daily driver -- was
 * shown "Admin role required." on her own work. `head_of_opsres` was
 * never read anywhere in the frontend at all.
 *
 * These mirror the backend dependencies rather than reimplementing
 * them: the API is still the gate, and a component using these is only
 * deciding whether to OFFER an affordance. Getting that wrong shows a
 * button that 403s, which is bad, but it is not an authorisation hole.
 */
import type { MeOut } from "shared/api/types";

export const OPSRES_ROLE = "head_of_opsres";
export const CRO_ROLE = "cro";
export const SERVICE_OWNER_ROLE = "service_owner";
export const BUSINESS_OWNER_ROLE = "business_owner";

/**
 * Round 10 F5 -- can this person reach this module at all?
 *
 * Mirrors `visible_modules`, which is the same answer `require_module`
 * gives. A `me` that carries no `modules` field is treated as holding
 * everything: that is the back-compat rule the backend itself uses for
 * a JWT with no modules claim, and it keeps an inline test mock from
 * silently hiding the whole product.
 */
export function hasModule(me: MeOut | undefined, module: string): boolean {
  if (!me) return false;
  if (me.modules === undefined) return true;
  return me.modules.includes(module);
}

export function hasRealmRole(me: MeOut | undefined, role: string): boolean {
  return me?.realm_roles?.includes(role) ?? false;
}

/** Mirrors `require_opsres_or_admin`. */
export function canRunOperations(me: MeOut | undefined): boolean {
  return (me?.is_admin ?? false) || hasRealmRole(me, OPSRES_ROLE);
}

/**
 * Can this viewer SIGN as `role`?
 *
 * Deliberately NOT admit-admin. A signature is the signer's own -- an
 * admin typing the CRO's name into a sealed ceremony is the governance
 * hole that dual sign-off exists to close.
 */
export function canSignAs(me: MeOut | undefined, role: string): boolean {
  return hasRealmRole(me, role);
}

/**
 * Mirrors `require_ceremony_reader` -- anyone who could be ASKED to
 * sign, plus admin.
 *
 * B2: the ceremony pages gated their whole render on
 * `canRunOperations`, so a signer following the link from her own
 * inbox met "Admin role required." on a decision waiting for her
 * signature. Reading is not signing: whether the sign control appears
 * is still `canSignAs`.
 *
 * A4: B2 fixed the realm ROLES and missed the second-line challenge,
 * which is held by a POSITION. A reviewer holding 27 MVB pack
 * challenges could record one and could not open the record to write
 * it about -- every row landed on "requires admin access".
 */
export const CEREMONY_SIGNATORY_ROLES = [
  CRO_ROLE,
  BUSINESS_OWNER_ROLE,
  SERVICE_OWNER_ROLE,
  "ae",
  // P429 -- the second seat on a Critical Internal Function.
  "cio",
  OPSRES_ROLE,
];

export function canReadCeremony(me: MeOut | undefined): boolean {
  return (
    (me?.is_admin ?? false) ||
    CEREMONY_SIGNATORY_ROLES.some((r) => hasRealmRole(me, r)) ||
    // The second line reads what it is asked to challenge. Not "any
    // position": the preparer authors the thing being challenged.
    (me?.positions ?? []).includes("reviewer")
  );
}

/** Mirrors `require_readiness_reader`. */
export function canReadReadiness(me: MeOut | undefined): boolean {
  return (
    (me?.is_admin ?? false) ||
    hasRealmRole(me, OPSRES_ROLE) ||
    (me?.positions ?? []).some((p) => p === "approver" || p === "reviewer")
  );
}

/**
 * Mirrors the board SNAPSHOT's guard in `dossier/board_router.py`:
 * `require_position("approver", "reviewer", or_realm_roles=("head_of_opsres",))`.
 *
 * Round 14 B4 -- this still read "approver only" after round 13 B2 opened
 * the route to the reviewer and the Head of Operational Resilience, so
 * the Executive page listed the board for neither: the route was open
 * and nothing in their navigation reached it. One rule, stated where the
 * server's is stated, and read by every surface that offers the board.
 */
export function canReadBoard(me: MeOut | undefined): boolean {
  if (me?.is_admin) return true;
  const positions = me?.positions ?? [];
  return (
    positions.includes("approver") ||
    positions.includes("reviewer") ||
    hasRealmRole(me, OPSRES_ROLE)
  );
}

/**
 * Mirrors the board PDF's `require_position("approver")`. Issuing the
 * signed pack stays the approver's act; the button is not offered to a
 * seat that may read the board but not issue it.
 */
export function canDownloadBoardPdf(me: MeOut | undefined): boolean {
  return (me?.is_admin ?? false) || (me?.positions ?? []).some((p) => p === "approver");
}

/**
 * Mirrors the readiness pack's `require_position("approver")`.
 *
 * D3 -- the Download-signed-pack button was shown to the Head of
 * Operational Resilience, who can read every figure on that page and
 * co-signs the assessment itself, and returned a silent 403. An
 * affordance that refuses the person it is offered to is worse than
 * one that is not offered: it reads as the product being broken
 * rather than as an authority boundary.
 *
 * Named separately from `canReadBoard` even though the rule is the
 * same today: two different endpoints, and a change to one must not
 * silently move the other.
 */
export function canDownloadReadinessPack(me: MeOut | undefined): boolean {
  return (me?.is_admin ?? false) || (me?.positions ?? []).some((p) => p === "approver");
}

/**
 * Does this person hold a role that makes them accountable for named
 * services or IBSes?
 *
 * Not "do they own any" -- that is a data question the estate answers,
 * and the home says so honestly when the answer is none. This is the
 * role question: whose desk is this.
 */
export function ownsSomething(me: MeOut | undefined): boolean {
  return hasRealmRole(me, SERVICE_OWNER_ROLE) || hasRealmRole(me, BUSINESS_OWNER_ROLE);
}
